import { Injectable, OnDestroy } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { createRxValue } from "@app/_helpers/utils";
import { environment } from "@env/environment";
import {
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	MyTimesQuery,
	MyTimesService,
	ProjectsQuery,
	ProjectsService,
	Time,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import {
	addDays,
	addMonths,
	differenceInDays,
	endOfToday,
	endOfISOWeek as endOfWeek,
	isFuture,
	isSameDay,
	isSameWeek,
	isWithinInterval,
	startOfToday,
	startOfISOWeek as startOfWeek,
	subDays,
	subMonths,
} from "date-fns/esm";
import { catchError, EMPTY, filter, finalize, map, Observable, Subject, Subscription, switchMap, tap } from "rxjs";

type RangeFetchCommand = { type: "fetch"; start: Date; end: Date; settle: () => void } | { type: "cancel" };

@Injectable({
	providedIn: "root",
})
export class HomeService implements OnDestroy {
	private readonly fetchCommands$ = new Subject<RangeFetchCommand>();
	private readonly fetchPipelineSub: Subscription;

	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private myTimesService: MyTimesService,
		private myTimesQuery: MyTimesQuery,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private projectsQuery: ProjectsQuery,
		private projectsService: ProjectsService
	) {
		// switchMap unsubscribes the previous HTTP call → no stale upserts after workspace switch / rapid range change
		this.fetchPipelineSub = this.fetchCommands$
			.pipe(switchMap((cmd) => (cmd.type === "cancel" ? EMPTY : this.loadRange$(cmd))))
			.subscribe();
	}

	ngOnDestroy() {
		this.fetchPipelineSub.unsubscribe();
		this.fetchCommands$.complete();
	}

	/**
	 * Cancel any in-flight range load (unsubscribes HttpClient request).
	 * Call before clearing stores on workspace switch.
	 */
	cancelFetches() {
		this.fetchCommands$.next({ type: "cancel" });
	}

	readonly range = createRxValue<DateRange<Date>>(new DateRange(startOfWeek(new Date()), endOfWeek(new Date())));

	readonly minRangeStart = subMonths(startOfToday(), 12);
	readonly maxRangeStart = addMonths(endOfToday(), 6);
	readonly viewDate$ = this.range.asObservable().pipe(filter((x) => !!x?.start && !!x.end));
	readonly viewDate$status = this.viewDate$.pipe(
		map(({ start, end }) => {
			const sameDay = isSameDay(start, end),
				sameWeek = isSameWeek(start, end),
				isFutureTime = isFuture(end),
				isValidRange = isWithinInterval(isFutureTime ? end : start, {
					start: this.minRangeStart,
					end: this.maxRangeStart,
				});
			return {
				start,
				end,
				isToday: isSameDay(start, end) && isSameDay(start, new Date()),
				sameDay,
				sameWeek,
				isValidRange,
				isFuture: isFutureTime,
			};
		})
	);

	prev() {
		const viewDate = this.range.value;
		let days = differenceInDays(viewDate.end, viewDate.start).clampThis(0) + 1;
		return this.range.update(new DateRange<Date>(subDays(viewDate.start, days), subDays(viewDate.end, days)));
	}
	today() {
		const now = new Date();
		return this.range.update(new DateRange<Date>(startOfWeek(now), endOfWeek(now)));
	}
	next() {
		const viewDate = this.range.value;
		let days = differenceInDays(viewDate.end, viewDate.start).clampThis(0) + 1;
		return this.range.update(new DateRange<Date>(addDays(viewDate.start, days), addDays(viewDate.end, days)));
	}
	selectWeek(date: Date) {
		const start = startOfWeek(date),
			end = endOfWeek(date);
		return this.range.update(new DateRange<Date>(start, end));
	}

	/** Queue a range load; previous in-flight request is cancelled via switchMap. */
	fetchBetween(start: Date, end: Date): Promise<void> {
		return new Promise<void>((resolve) => {
			this.fetchCommands$.next({
				type: "fetch",
				start,
				end,
				settle: resolve,
			});
		});
	}

	private loadRange$(cmd: Extract<RangeFetchCommand, { type: "fetch" }>): Observable<unknown> {
		const { start, end, settle } = cmd;
		const user = this.userSettingsQuery.getValue();
		const workspaceId = user.workspace?.id;
		if (!workspaceId || !user.id) {
			settle();
			return EMPTY;
		}

		const isWork = (user.settings?.tableDisplay as any) === "ComeGo";
		const settleOnce = () => {
			settle();
		};

		if (isWork) {
			const store = this.comegoQuery.__store__ as ComegoStore;
			store.setLoading(true);
			const url =
				environment.serverUrl +
				`/get/comego?$filter=start ge '${start.toISOString()}' and start le '${end.toISOString()}' and user__id eq '${user.id}'`;
			return this.comegoService.adapter.get<ComegoTime[]>(url).pipe(
				tap((comego) => {
					store.remove((x) => !x.workspace || x.workspace.id !== workspaceId);
					store.upsertMany(comego ?? []);
				}),
				catchError((err) => {
					pushError(err);
					return EMPTY;
				}),
				finalize(() => {
					store.setLoading(false);
					settleOnce();
				})
			);
		}

		const store = this.myTimesQuery.__store__ as MyTimesStore;
		store.setLoading(true);
		const url =
			environment.serverUrl +
			`/get/times?$filter=start ge '${start.toISOString()}' and start le '${end.toISOString()}' and user__id eq '${user.id}'`;
		return this.myTimesService.adapter.get<Time[]>(url).pipe(
			switchMap((times) => {
				store.remove((x) => !x.workspace || x.workspace.id !== workspaceId);
				store.upsertMany(times ?? []);
				return this.ensureMissingProjectsLoaded$(times ?? []);
			}),
			catchError((err) => {
				pushError(err);
				return EMPTY;
			}),
			finalize(() => {
				store.setLoading(false);
				settleOnce();
			})
		);
	}

	/** Times API embeds a slim project (no color). Completed projects are not in the store until loaded. */
	private ensureMissingProjectsLoaded$(times: Time[]): Observable<unknown> {
		const needsCompletedProjects = times?.some((t) => t.project?.id && !this.projectsQuery.hasEntity(t.project.id));
		if (!needsCompletedProjects) return EMPTY;
		return this.projectsService.get(true).pipe(catchError(() => EMPTY));
	}
}
